import { useCallback, useEffect, useMemo, useState } from 'react';
import { MakeTag } from '../lib/jobMakes.tsx';
import { api, ApiError, fmt, fmtDate, fmtTimestamp } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import BoardStockCheck from '../components/BoardStockCheck.tsx';
import CustomerBoard from '../components/CustomerBoard.tsx';
import BoardToOrder, { OrderBoard } from '../components/BoardToOrder.tsx';
import SearchSelect from '../components/SearchSelect.tsx';
import UndoDialog from '../components/UndoDialog.tsx';
import { useList } from '../lib/useList.ts';

/*
 * Board as stock. A list of boards (type + GSM + sheet size), each with a
 * ledger of every sheet received, issued, returned, wasted or adjusted. On hand
 * is the sum of that ledger; nothing on this screen edits a quantity directly.
 */

interface BoardItem {
  item_id: number; name: string; board_type_id: number; board_type: string;
  gsm: string; length_cm: string; width_cm: string;
  min_level: string | null; notes: string | null; active: boolean;
  on_hand: string; received_total: string; last_received: string | null;
  below_min: boolean;
  /** Stocked in for jobs not yet released (053), and what is left for anyone. */
  paired: string; free: string;
}

interface NeedJob {
  job_id: number; job_number: string; customer_name: string; sheets: string; paired: string;
  staged: boolean; delivery_date: string | null;
  on_order: string; on_order_due: string | null;
}
interface ComingOrder {
  order_id: number; board_order: string; supplier_name: string; sheets: string; received: string; to_come: string;
  ordered_on: string; expected_date: string; late: boolean;
  closed_at: string | null; closed_on: string | null; closed_reason: string | null;
}
interface UsingBox {
  id: number; name: string; customer_name: string; box_size: string | null; ups: number | null;
  last_ordered: string | null; times_ordered: string;
}
type DetailTab = 'jobs' | 'coming' | 'boxes' | 'history';
const BOX_PAGE = 10;
const HISTORY_MORE = 20;

interface Movement {
  movement_id: number; item_id: number; item_name: string;
  movement_type: LedgerType; change: string; balance_after: string;
  movement_date: string; job_id: number | null; job_number: string | null;
  supplier_name: string | null; bill_no: string | null; unit_price: string | null;
  reason: string | null; entered_by_name: string; created_at: string;
  reverses_id: number | null; reversed_by_id: number | null;
}

interface BoardType { id: number; name: string; active: boolean }
interface Named { id: number; name: string }
interface JobOption { job_id: number; job_number: string; customer_name: string; box_name: string }

type MovementType = 'RECEIVE' | 'ISSUE' | 'RETURN' | 'WASTE' | 'ADJUST';
/** What the history can hold: the movements people record, plus board moved
 *  between jobs when one is sent on without its own (066). */
type LedgerType = MovementType | 'TRANSFER';

/** How each movement reads in the history. */
const MOVE_LABEL: Record<LedgerType, string> = {
  RECEIVE: 'Stock in', ISSUE: 'Issued to floor', RETURN: 'Returned', WASTE: 'Wastage', ADJUST: 'Count corrected',
  TRANSFER: 'Between jobs',
};
const MOVE_TAG: Record<LedgerType, string> = {
  RECEIVE: 'ready', ISSUE: 'blocked', RETURN: 'high', WASTE: 'late', ADJUST: 'soon', TRANSFER: 'blocked',
};

function MoveTag({ m }: { m: Movement }) {
  if (m.reverses_id) return <span className="tag blocked">Undo</span>;
  if (m.movement_type === 'TRANSFER' && m.reason?.startsWith('Held from stock')) {
    return <span className="tag ready">Held for job</span>;
  }
  return <span className={`tag ${MOVE_TAG[m.movement_type]}`}>{MOVE_LABEL[m.movement_type]}</span>;
}
/** What each action button says, and the dialog title it opens. */
const ACTION: Record<MovementType, { button: string; title: string }> = {
  RECEIVE: { button: 'Stock in', title: 'Stock in' },
  ISSUE: { button: 'Issue to floor', title: 'Issue to floor' },
  RETURN: { button: 'Return to stock', title: 'Return to stock' },
  WASTE: { button: 'Wastage', title: 'Record wastage' },
  ADJUST: { button: 'Correct count', title: 'Correct the count' },
};

const today = () => {
  // The local day, not toISOString's UTC day -- which is yesterday for the
  // first five and a half hours of every IST morning.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const signed = (v: string | number) => {
  const n = Number(v);
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmt(Math.abs(n))}`;
};

/** What is set aside for one job: paired to it, and on order for it. Facts only. */
function NeedTag({ j }: { j: NeedJob }) {
  const sheets = Number(j.sheets);
  const paired = Number(j.paired);
  const onOrder = Number(j.on_order);
  if (paired >= sheets) return <span className="tag ready">Paired</span>;
  const parts = [
    paired > 0 ? `Paired ${fmt(j.paired)}` : '',
    onOrder > 0 ? `On order ${fmt(j.on_order)}${j.on_order_due ? ` · due ${fmtDate(j.on_order_due)}` : ''}` : '',
  ].filter(Boolean);
  return parts.length
    ? <span className="tag waiting">{parts.join(' · ')}</span>
    : <span className="tag blocked">Not paired</span>;
}

/** The state a board is in, as one tag. */
function LevelTag({ item }: { item: BoardItem }) {
  if (!item.active) return <span className="tag blocked">Not in use</span>;
  if (Number(item.on_hand) <= 0) return <span className="tag late">Out of stock</span>;
  if (item.below_min) return <span className="tag waiting">Low</span>;
  return <span className="tag ready">In stock</span>;
}

export default function BoardStock({
  canManage, setError, setNotice,
}: {
  canManage: boolean;
  setError: (s: string) => void;
  setNotice: (s: string) => void;
}) {
  const [tab, setTab] = useState<'stock' | 'order' | 'customer' | 'check' | 'history'>('order');
  // Bumped whenever stock changes, so the other tabs re-read when shown.
  const [version, setVersion] = useState(0);
  const changed = () => setVersion((v) => v + 1);

  const tabs: { key: typeof tab; label: string }[] = [
    { key: 'order', label: 'To order' },
    { key: 'stock', label: 'Stock' },
    { key: 'customer', label: 'Customer\'s' },
    { key: 'check', label: 'Stock check' },
    { key: 'history', label: 'History' },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 0' }} role="tablist">
        {tabs.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key}
                  className={`btn${tab === t.key ? ' primary' : ''}`}
                  onClick={() => { setError(''); setTab(t.key); }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'stock' && (
        <StockTab canManage={canManage} setError={setError} setNotice={setNotice}
                  version={version} onChanged={changed} />
      )}
      {tab === 'order' && (
        <BoardToOrder canManage={canManage} setError={setError} setNotice={setNotice} onChanged={changed} />
      )}
      {tab === 'customer' && (
        <CustomerBoard canManage={canManage} setError={setError} setNotice={setNotice} onChanged={changed} />
      )}
      {tab === 'check' && (
        <BoardStockCheck canManage={canManage} setError={setError} setNotice={setNotice} onChanged={changed} />
      )}
      {tab === 'history' && <HistoryTab key={version} />}
    </>
  );
}

// ------------------------------------------------------------------- stock

function StockTab({ canManage, setError, setNotice, version, onChanged }: {
  canManage: boolean; setError: (s: string) => void; setNotice: (s: string) => void;
  version: number; onChanged: () => void;
}) {
  const [items, setItems] = useState<BoardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [receiving, setReceiving] = useState(false);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (showRetired) qs.set('includeRetired', 'true');
      const res = await api.get<{ items: BoardItem[] }>(`/api/stock/board-items?${qs}`);
      setItems(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load board stock');
    } finally {
      setLoading(false);
    }
  }, [search, showRetired, setError]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load, version]);

  const totals = useMemo(() => ({
    boards: items.filter((i) => i.active).length,
    sheets: items.reduce((n, i) => n + (i.active ? Number(i.on_hand) : 0), 0),
    low: items.filter((i) => i.active && (i.below_min || Number(i.on_hand) <= 0)).length,
  }), [items]);

  const done = (msg: string) => { setNotice(msg); onChanged(); void load(); };

  return (
    <>
      <div className="panel-head" style={{ flexWrap: 'wrap', borderBottom: 0 }}>
        <span className="count">
          {loading ? 'loading…'
            : `${totals.boards} board${totals.boards === 1 ? '' : 's'} · ${fmt(totals.sheets)} sheets on hand`
              + (totals.low ? ` · ${totals.low} low or out` : '')}
        </span>
        <span className="spacer" />
        <input className="search" placeholder="Search boards" value={search}
               onChange={(e) => setSearch(e.target.value)} />
        <label className="dim" style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" aria-label="Show boards not in use" checked={showRetired}
                 onChange={(e) => setShowRetired(e.target.checked)} />
          Show boards not in use
        </label>
        {canManage && (
          <button className="btn primary" onClick={() => setReceiving(true)}>Stock in</button>
        )}
      </div>

      {!loading && items.length === 0 ? (
        <div className="empty">
          {search ? 'No board matches that search.'
            : 'No boards yet. Click Stock in to record the first delivery — the board is set up as you go.'}
        </div>
      ) : (
        <div style={{ padding: '0 16px 16px' }}>
          <div className="order-list">
            <table className="order-table">
              <colgroup>
                <col /><col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} />
                <col style={{ width: 120 }} /><col style={{ width: 130 }} /><col style={{ width: 140 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Board</th>
                  <th className="num">On hand</th>
                  <th className="num" title="Stocked in for a job that has not gone to production yet">Paired</th>
                  <th className="num">Free</th>
                  <th className="num">Reorder at</th>
                  <th>Status</th>
                  <th>Last stock in</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.item_id} className="clickable" style={{ opacity: i.active ? 1 : 0.6 }}
                      onClick={() => setOpenId(i.item_id)}>
                    <td>
                      <button className="name-link" onClick={(e) => { e.stopPropagation(); setOpenId(i.item_id); }}>
                        {i.name}
                      </button>
                    </td>
                    <td className="num strong">{fmt(i.on_hand)}</td>
                    <td className="num dim">{Number(i.paired) > 0 ? fmt(i.paired) : '—'}</td>
                    <td className="num">{fmt(i.free)}</td>
                    <td className="num dim">{i.min_level === null ? '—' : fmt(i.min_level)}</td>
                    <td><LevelTag item={i} /></td>
                    <td className="dim">{i.last_received ? fmtDate(i.last_received) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openId !== null && (
        <BoardDetail itemId={openId} canManage={canManage}
                     onClose={() => setOpenId(null)} onDone={done} />
      )}
      {receiving && (
        <MovementDialog type="RECEIVE" items={items.filter((i) => i.active)}
                        onClose={() => setReceiving(false)}
                        onDone={(msg) => { setReceiving(false); done(msg); }} />
      )}
    </>
  );
}

// ------------------------------------------------------- one board, opened

function BoardDetail({ itemId, canManage, onClose, onDone }: {
  itemId: number; canManage: boolean; onClose: () => void; onDone: (msg: string) => void;
}) {
  const [item, setItem] = useState<BoardItem | null>(null);
  const [rows, setRows] = useState<Movement[]>([]);
  const [jobs, setJobs] = useState<NeedJob[]>([]);
  const [coming, setComing] = useState<ComingOrder[]>([]);
  const [boxes, setBoxes] = useState<UsingBox[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState<DetailTab | null>(null);
  const [boxSearch, setBoxSearch] = useState('');
  const [boxShown, setBoxShown] = useState(BOX_PAGE);
  const [menu, setMenu] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [error, setError] = useState('');
  const [acting, setActing] = useState<MovementType | null>(null);
  const [editingMin, setEditingMin] = useState(false);
  const [minDraft, setMinDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [undoUrl, setUndoUrl] = useState<string | null>(null);
  const openOrders = coming.filter((c) => !c.closed_at);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{
        item: BoardItem; movements: Movement[]; movementsTotal: number;
        jobs: NeedJob[]; coming: ComingOrder[]; boxes: UsingBox[];
      }>(`/api/stock/board-items/${itemId}`);
      setJobs(res.jobs);
      setComing(res.coming);
      setBoxes(res.boxes);
      setItem(res.item);
      setRows(res.movements);
      setRowsTotal(res.movementsTotal);
      setTab((t) => t ?? (res.jobs.length ? 'jobs' : res.coming.some((c) => !c.closed_at) ? 'coming' : 'history'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this board');
    }
  }, [itemId]);

  async function moreHistory() {
    setLoadingMore(true);
    try {
      const res = await api.get<{ movements: Movement[]; total: number }>(
        `/api/stock/board-items/${itemId}/movements?offset=${rows.length}&limit=${HISTORY_MORE}`);
      setRows((r) => [...r, ...res.movements]);
      setRowsTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load more history');
    } finally { setLoadingMore(false); }
  }

  useEffect(() => { void load(); }, [load]);

  async function save(patch: Record<string, unknown>, msg: string) {
    setBusy(true);
    setError('');
    try {
      await api.patch(`/api/stock/board-items/${itemId}`, patch);
      await load();
      onDone(msg);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  const onOrder = coming.reduce((n, c) => n + Number(c.to_come), 0);
  const more: MovementType[] = Number(item?.on_hand ?? 0) > 0
    ? ['ISSUE', 'RETURN', 'WASTE', 'ADJUST'] : ['RETURN', 'ADJUST'];

  function stopUsing() {
    if (!item) return;
    const warn = [
      boxes.length ? `${boxes.length} ${boxes.length === 1 ? 'box uses' : 'boxes use'} this board — their next orders will need another board.` : '',
      jobs.length ? `${jobs.length} ${jobs.length === 1 ? 'job still names' : 'jobs still name'} it on the card.` : '',
    ].filter(Boolean).join(' ');
    if (warn && !window.confirm(`${warn} Stop using ${item.name}?`)) return;
    void save({ active: false }, `${item.name} marked not in use`);
  }

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 960 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head" style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <h3>{item?.name ?? 'Board'}</h3>
            <p style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {item ? <>
                <LevelTag item={item} />
                <span className="dim" style={{ fontSize: 12.5 }}>
                  {item.last_received ? `Last stock in ${fmtDate(item.last_received)}` : 'Never stocked in'}
                </span>
              </> : 'loading…'}
            </p>
          </div>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

          {item && (
            <>
              <div className="figures">
                <div><span>On hand</span><strong>{fmt(item.on_hand)}</strong></div>
                <div title="Stocked in for a job that has not gone to production yet">
                  <span>Paired to jobs</span><strong>{fmt(item.paired)}</strong>
                </div>
                <div title="The first job to go to production takes it"><span>Free</span><strong>{fmt(item.free)}</strong></div>
                <div>
                  <span>On order</span><strong>{fmt(String(onOrder))}</strong>
                  {coming[0] && <em>next due {fmtDate(coming[0].expected_date)}</em>}
                </div>
                <div>
                  <span>Reorder at</span>
                  {editingMin ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="number" min="0" value={minDraft} autoFocus aria-label="Reorder level"
                             style={{ width: 90 }} onChange={(e) => setMinDraft(e.target.value)}
                             onKeyDown={(e) => { if (e.key === 'Escape') setEditingMin(false); }} />
                      <button className="btn primary" disabled={busy}
                              onClick={() => {
                                setEditingMin(false);
                                void save({ minLevel: minDraft.trim() === '' ? null : Math.round(Number(minDraft)) },
                                          'Reorder level saved');
                              }}>Save</button>
                    </div>
                  ) : (
                    <strong>
                      {item.min_level === null ? '—' : fmt(item.min_level)}
                      {canManage && (
                        <button className="linkish" style={{ marginLeft: 8, fontSize: 12.5, fontWeight: 400 }}
                                onClick={() => { setMinDraft(item.min_level ?? ''); setEditingMin(true); }}>
                          {item.min_level === null ? 'Set' : 'Change'}
                        </button>
                      )}
                    </strong>
                  )}
                </div>
              </div>

              {canManage && item.active && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                  <button className="btn primary" onClick={() => setActing('RECEIVE')}>Stock in</button>
                  <button className="btn" onClick={() => setOrdering(true)}>Order</button>
                  <span className="menu-wrap">
                    <button className="btn" aria-expanded={menu} onClick={() => setMenu((v) => !v)}>More ▾</button>
                    {menu && (
                      <>
                        <div className="menu-scrim" onClick={() => setMenu(false)} />
                        <div className="menu" role="menu" style={{ left: 0, right: 'auto' }}>
                          {more.map((a) => (
                            <button key={a} role="menuitem" onClick={() => { setMenu(false); setActing(a); }}>
                              {ACTION[a].button}
                            </button>
                          ))}
                          <div className="menu-sep" />
                          <button role="menuitem" title="Hide it from the board list; its history is kept"
                                  onClick={() => { setMenu(false); stopUsing(); }}>
                            Stop using this board
                          </button>
                        </div>
                      </>
                    )}
                  </span>
                </div>
              )}
              {canManage && !item.active && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                  <span className="dim">Not in use: hidden from the board list, and no stock can be recorded against it. Its history is kept.</span>
                  <button className="btn" disabled={busy}
                          onClick={() => void save({ active: true }, `${item.name} back in use`)}>
                    Use again
                  </button>
                </div>
              )}

              <div className="detail-tabs" role="tablist">
                {([
                  ['jobs', 'Jobs needing it', jobs.length],
                  ['coming', 'On order', openOrders.length],
                  ['boxes', 'Boxes using it', boxes.length],
                  ['history', 'History', rowsTotal],
                ] as [DetailTab, string, number][]).map(([key, label, n]) => (
                  <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
                    {label}<span className="n">{fmt(String(n))}</span>
                  </button>
                ))}
              </div>

              <div className="detail-pane">
                {tab === 'jobs' && (jobs.length === 0 ? (
                  <div className="pane-empty">No job card names this board.</div>
                ) : (
                  <div className="order-list">
                    <table className="order-table">
                      <colgroup>
                        <col style={{ width: 90 }} /><col style={{ width: 110 }} /><col /><col style={{ width: 100 }} />
                        <col style={{ width: 200 }} /><col style={{ width: 120 }} />
                      </colgroup>
                      <thead>
                        <tr><th>Job</th><th>Customer</th><th className="num">Sheets</th><th>Status</th><th>Due</th></tr>
                      </thead>
                      <tbody>
                        {jobs.map((j) => (
                          <tr key={j.job_id}>
                            <td className="strong">{j.job_number}<MakeTag number={j.job_number} /></td>
                            <td>{j.customer_name}</td>
                            <td className="num strong">{fmt(j.sheets)}</td>
                            <td><NeedTag j={j} /></td>
                            <td className="dim">
                              {j.staged ? 'Staged' : j.delivery_date ? fmtDate(j.delivery_date) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

                {tab === 'coming' && (coming.length === 0 ? (
                  <div className="pane-empty">
                    Nothing on order.
                    {canManage && item.active && (
                      <button className="btn" style={{ marginLeft: 10 }} onClick={() => setOrdering(true)}>Order</button>
                    )}
                  </div>
                ) : (
                  <div className="order-list">
                    <table className="order-table">
                      <colgroup>
                        <col style={{ width: 90 }} /><col style={{ width: 110 }} /><col /><col style={{ width: 100 }} />
                        <col style={{ width: 200 }} /><col style={{ width: 120 }} />
                        {canManage && <col style={{ width: 90 }} />}
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Order</th><th>Ordered</th><th>Supplier</th><th className="num">To come</th><th>Received</th><th>Expected</th>
                          {canManage && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {coming.map((c) => (
                          <tr key={c.order_id} className={c.closed_at ? 'dim' : undefined}>
                            <td className="mono">{c.board_order}</td>
                            <td className="dim">{fmtDate(c.ordered_on)}</td>
                            <td>{c.supplier_name}</td>
                            <td className="num strong">{c.closed_at ? '—' : fmt(c.to_come)}</td>
                            <td className="dim">{Number(c.received) > 0 ? `${fmt(c.received)} of ${fmt(c.sheets)}` : 'None yet'}</td>
                            <td>
                              {c.closed_at
                                ? <span className="tag blocked" title={c.closed_reason ?? undefined}>Closed · {fmtDate(c.closed_on)}</span>
                                : c.late ? <span className="tag late">Late · {fmtDate(c.expected_date)}</span>
                                : <span className="dim">{fmtDate(c.expected_date)}</span>}
                            </td>
                            {canManage && (
                              <td className="num">
                                {c.closed_at && (
                                  <button className="btn" onClick={() => setUndoUrl(`/api/stock/board-orders/${c.order_id}/reopen`)}>
                                    Reopen
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}

                {tab === 'boxes' && (boxes.length === 0 ? (
                  <div className="pane-empty">
                    No box uses this board yet. A box takes its board from the last card it was made on.
                  </div>
                ) : (() => {
                  const q = boxSearch.trim().toLowerCase();
                  const found = q ? boxes.filter((b) => `${b.name} ${b.customer_name}`.toLowerCase().includes(q)) : boxes;
                  return (
                    <>
                      {boxes.length > BOX_PAGE && (
                        <input className="search" style={{ marginBottom: 10, width: 260 }} placeholder="Search boxes or customers"
                               value={boxSearch} onChange={(e) => { setBoxSearch(e.target.value); setBoxShown(BOX_PAGE); }} />
                      )}
                      {found.length === 0 ? (
                        <div className="pane-empty">No box matches “{boxSearch}”.</div>
                      ) : (
                        <div className="order-list">
                          <table className="order-table">
                            <colgroup>
                              <col /><col style={{ width: '26%' }} /><col style={{ width: 150 }} />
                              <col style={{ width: 70 }} /><col style={{ width: 150 }} />
                            </colgroup>
                            <thead>
                              <tr><th>Box</th><th>Customer</th><th>Size</th><th className="num">Ups</th><th>Last ordered</th></tr>
                            </thead>
                            <tbody>
                              {found.slice(0, boxShown).map((b) => (
                                <tr key={b.id}>
                                  <td className="strong">{b.name}</td>
                                  <td>{b.customer_name}</td>
                                  <td className="dim">{b.box_size ?? '—'}</td>
                                  <td className="num dim">{b.ups ?? '—'}</td>
                                  <td className="dim">
                                    {b.last_ordered
                                      ? `${fmtDate(b.last_ordered)} · ${fmt(b.times_ordered)} ${Number(b.times_ordered) === 1 ? 'order' : 'orders'}`
                                      : 'Never'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {found.length > boxShown && (
                        <div className="pane-foot">
                          <span>Showing {fmt(String(boxShown))} of {fmt(String(found.length))}</span>
                          <button className="btn" onClick={() => setBoxShown((n) => n + BOX_PAGE)}>
                            Show {Math.min(BOX_PAGE, found.length - boxShown)} more
                          </button>
                        </div>
                      )}
                    </>
                  );
                })())}

                {tab === 'history' && (rows.length === 0 ? (
                  <div className="pane-empty">Nothing recorded yet. Stock in the first delivery to start.</div>
                ) : (
                  <>
                    <div className="order-list">
                      <table className="order-table">
                        <colgroup>
                          <col style={{ width: 110 }} /><col style={{ width: 150 }} /><col style={{ width: 80 }} />
                          <col style={{ width: 85 }} /><col style={{ width: 90 }} /><col /><col />
                          <col style={{ width: 100 }} />
                          {canManage && <col style={{ width: 80 }} />}
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Date</th><th>What</th><th className="num">Sheets</th>
                            <th className="num">Balance</th><th>Job</th><th>Supplier · bill</th>
                            <th>Reason</th><th>By</th>
                            {canManage && <th />}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((m) => (
                            <tr key={m.movement_id} className={m.reversed_by_id ? 'struck' : undefined}>
                              <td className="dim">{fmtDate(m.movement_date)}</td>
                              <td><MoveTag m={m} /></td>
                              <td className="num"><strong>{signed(m.change)}</strong></td>
                              <td className="num dim">{fmt(m.balance_after)}</td>
                              <td className="strong">{m.job_number ?? <span className="dim">—</span>}<MakeTag number={m.job_number} /></td>
                              <td className="dim">
                                {m.supplier_name ?? '—'}
                                {m.bill_no ? ` · ${m.bill_no}` : ''}
                                {m.unit_price ? ` · ₹${Number(m.unit_price).toFixed(2)}/kg` : ''}
                              </td>
                              <td className="dim">{m.reason ?? '—'}</td>
                              <td className="dim" title={fmtTimestamp(m.created_at)}>{m.entered_by_name}</td>
                              {canManage && (
                                <td className="num">
                                  {!m.reverses_id && !m.reversed_by_id && (
                                    <button className="btn" onClick={() => setUndoUrl(`/api/stock/board-movements/${m.movement_id}/undo`)}>
                                      Undo
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {rowsTotal > rows.length && (
                      <div className="pane-foot">
                        <span>Showing {fmt(String(rows.length))} of {fmt(String(rowsTotal))}, newest first</span>
                        <button className="btn" disabled={loadingMore} onClick={() => void moreHistory()}>
                          {loadingMore ? 'Loading…' : `Show ${Math.min(HISTORY_MORE, rowsTotal - rows.length)} more`}
                        </button>
                      </div>
                    )}
                  </>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {ordering && item && (
        <OrderBoard itemId={item.item_id} name={item.name} onClose={() => setOrdering(false)}
                    onDone={(msg) => { setOrdering(false); void load(); onDone(msg); }} />
      )}
      {acting && item && (
        <MovementDialog type={acting} items={[item]} fixedItemId={item.item_id}
                        onClose={() => setActing(null)}
                        onDone={(msg) => { setActing(null); void load(); onDone(msg); }} />
      )}
      {undoUrl && (
        <UndoDialog previewUrl={undoUrl} onClose={() => setUndoUrl(null)}
                    onDone={(msg) => { void load(); onDone(msg); }} />
      )}
    </div>
  );
}

// ------------------------------------------------------- record a movement

/** The board picker's value for "this is a board we have not had before". */
const NEW_BOARD = '__new';

function MovementDialog({ type, items, fixedItemId, onClose, onDone }: {
  type: MovementType;
  items: BoardItem[];
  /** Opened from a board: the board is already chosen. */
  fixedItemId?: number;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  /*
   * Stock in is the one place a new board is set up. A delivery of a board
   * never had before is "+ New board" in the picker -- type, GSM and size --
   * rather than a separate set-up step first. With no boards yet at all, the
   * form starts there.
   */
  const canAddNew = type === 'RECEIVE' && !fixedItemId;
  const [itemId, setItemId] = useState<string>(
    fixedItemId ? String(fixedItemId) : canAddNew && items.length === 0 ? NEW_BOARD : '');
  const [created, setCreated] = useState<BoardItem | null>(null);
  const [qty, setQty] = useState('');
  const [date, setDate] = useState(today());
  const [direction, setDirection] = useState<'IN' | 'OUT'>('OUT');
  const [supplierId, setSupplierId] = useState('');
  const [billNo, setBillNo] = useState('');
  const [price, setPrice] = useState('');
  const [jobId, setJobId] = useState('');
  const [reason, setReason] = useState('');
  const [suppliers, setSuppliers] = useState<Named[]>([]);
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [error, setError] = useState('');
  /*
   * The server stopped this because it takes sheets stocked in for a waiting
   * job. Sheets that are damaged or miscounted are gone whoever they were for,
   * so it can go through -- but only once the person has read whose they were.
   */
  const [pairedWarning, setPairedWarning] = useState('');
  const [busy, setBusy] = useState(false);

  // A new board's details.
  const [types, setTypes] = useState<BoardType[]>([]);
  const [typeId, setTypeId] = useState('');
  const [newType, setNewType] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [gsm, setGsm] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');

  useEffect(() => {
    if (type === 'RECEIVE') {
      api.get<{ suppliers: Named[] }>('/api/reference/suppliers')
        .then((r) => setSuppliers(r.suppliers)).catch(() => {});
    }
    if (type !== 'ADJUST') {
      api.get<{ jobs: JobOption[] }>('/api/stock/board-movements/jobs')
        .then((r) => setJobs(r.jobs)).catch(() => {});
    }
    if (canAddNew) {
      api.get<{ types: BoardType[] }>('/api/stock/board-types')
        .then((r) => {
          const active = r.types.filter((t) => t.active);
          setTypes(active);
          if (active.length === 1) setTypeId(String(active[0]!.id));
        }).catch(() => {});
    }
  }, [type, canAddNew]);

  const isNew = itemId === NEW_BOARD;
  const all = created ? [...items, created] : items;
  const item = isNew ? undefined : all.find((i) => String(i.item_id) === itemId);

  const boardType = types.find((t) => String(t.id) === typeId);
  const l = Number(length);
  const w = Number(width);
  const newName = boardType && Number(gsm) > 0 && l > 0 && w > 0
    ? `${boardType.name} · ${Number(gsm)} GSM · ${Math.max(l, w)} × ${Math.min(l, w)} cm`
    : '';
  // Typed in as new but already in the list: use that one, don't make a twin.
  const existing = isNew && newName ? items.find((i) => i.name === newName) : undefined;
  const target = existing ?? item;

  const n = Number(qty);
  const takesAway = type === 'ISSUE' || type === 'WASTE' || (type === 'ADJUST' && direction === 'OUT');
  const onHandNow = target ? Number(target.on_hand) : 0;
  const after = target || isNew ? onHandNow + (takesAway ? -n : n) : null;

  const problem =
    isNew && !boardType ? 'Choose the board type'
    : isNew && !(Number(gsm) > 0) ? 'Enter the GSM'
    : isNew && !(l > 0 && w > 0) ? 'Enter the sheet size'
    : !isNew && !item ? 'Choose the board'
    : !qty || !Number.isInteger(n) || n <= 0 ? 'Enter the number of sheets'
    : after !== null && after < 0 ? `Only ${fmt(onHandNow)} sheets on hand`
    : type === 'ADJUST' && !reason.trim() ? 'Give a reason for the correction'
    : '';

  async function saveType() {
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ type: BoardType }>('/api/stock/board-types', { name: newType.trim() });
      setTypes((t) => [...t, res.type].sort((a, b) => a.name.localeCompare(b.name)));
      setTypeId(String(res.type.id));
      setNewType('');
      setAddingType(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the type');
    } finally {
      setBusy(false);
    }
  }

  async function submit(takePaired = false) {
    if (problem) return;
    setBusy(true);
    setError('');
    setPairedWarning('');
    try {
      let board = target;
      if (!board) {
        // A board we have not had before: add it to the list first. Kept once
        // made, so if the stock-in itself is refused and corrected, a second
        // try does not try to add the same board again.
        const res = await api.post<{ item: BoardItem }>('/api/stock/board-items', {
          boardTypeId: Number(typeId), gsm: Number(gsm), lengthCm: l, widthCm: w,
        });
        board = res.item;
        setCreated(res.item);
        setItemId(String(res.item.item_id));
      }
      const res = await api.post<{
        onHand: number; pairedFor: string | null;
      }>(`/api/stock/board-items/${board.item_id}/movements`, {
        type,
        quantity: n,
        movementDate: date,
        ...(type === 'ADJUST' ? { direction } : {}),
        ...(jobId ? { jobId: Number(jobId) } : {}),
        ...(type === 'RECEIVE' && supplierId ? { supplierId: Number(supplierId) } : {}),
        ...(type === 'RECEIVE' && billNo.trim() ? { billNo: billNo.trim() } : {}),
        ...(type === 'RECEIVE' && price.trim() ? { unitPrice: Number(price) } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(takePaired ? { takePaired: true } : {}),
      });
      let msg = `${MOVE_LABEL[type]}: ${fmt(n)} sheets · ${board.name} · ${fmt(res.onHand)} on hand`;
      if (res.pairedFor) msg += ` · paired to ${res.pairedFor}`;
      onDone(msg);
    } catch (err) {
      if (err instanceof ApiError && (err.details as { needsConfirm?: boolean } | undefined)?.needsConfirm) {
        setPairedWarning(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Could not record that');
      }
    } finally {
      setBusy(false);
    }
  }

  // A changed figure is a different movement: the warning was about the old one.
  useEffect(() => { setPairedWarning(''); }, [qty, itemId, jobId, direction]);

  const heading = fixedItemId && target
    ? <>{target.name} · <strong>{fmt(target.on_hand)} sheets on hand</strong></>
    : null;

  return (
    // Opened over a board's detail view: a click outside closes this dialog
    // only, not the board behind it as well.
    <div className="backdrop"
         // Over a board's detail view the page is already dimmed once; a
         // second full dimming made it near black.
         style={{ zIndex: 30, ...(fixedItemId ? { background: 'rgba(0, 0, 0, .25)', backdropFilter: 'none' } : {}) }}
         onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal" style={{ width: 540 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{ACTION[type].title}</h3>
          {heading && <p>{heading}</p>}
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {pairedWarning && (
            <div className="tag waiting notice" style={{ marginBottom: 12 }}>{pairedWarning}</div>
          )}

          {!fixedItemId && (
            <div className="field">
              <label htmlFor="mv-item" className="req">Board</label>
              {/* The button sits beside the list rather than as its last
                  option: once there are many boards, an entry at the bottom of
                  a long list is one nobody finds. */}
              <div style={{ display: 'flex', gap: 8 }}>
                {isNew ? (
                  <div className="dim" style={{
                    flex: 1, display: 'flex', alignItems: 'center', padding: '0 12px',
                    border: '1px dashed var(--line)', borderRadius: 8, fontSize: 13.5,
                  }}>
                    New board — enter its details below
                  </div>
                ) : (
                  <SearchSelect id="mv-item" autoFocus value={itemId} onChange={setItemId}
                                placeholder="Type to search, e.g. 250 or 61"
                                emptyLabel="Choose a board…"
                                options={all.map((i) => ({ value: String(i.item_id), label: i.name }))} />
                )}
                {canAddNew && (
                  <button className="btn" style={{ whiteSpace: 'nowrap' }}
                          onClick={() => setItemId(isNew ? '' : NEW_BOARD)}>
                    {isNew ? 'Choose existing' : 'New board'}
                  </button>
                )}
              </div>
              {item && (
                <div className="hint" style={{ fontSize: 13.5 }}>
                  <span style={{ fontWeight: 600 }}>{fmt(item.on_hand)}</span> sheets on hand
                </div>
              )}
            </div>
          )}

          {isNew && (
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '14px 14px 2px', marginBottom: 14 }}>
              <div className="field">
                <label htmlFor="nb-type" className="req">Board type</label>
                {addingType ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input id="nb-type" type="text" value={newType} autoFocus placeholder="e.g. White Back"
                           onChange={(e) => setNewType(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter' && newType.trim()) void saveType(); }} />
                    <button className="btn primary" disabled={busy || !newType.trim()} onClick={saveType}>Add</button>
                    <button className="btn" onClick={() => { setAddingType(false); setNewType(''); }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select id="nb-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                      <option value="">Choose…</option>
                      {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button className="btn" onClick={() => setAddingType(true)}>New type</button>
                  </div>
                )}
              </div>
              <div className="row2">
                <div className="field">
                  <label htmlFor="nb-gsm" className="req">GSM</label>
                  <input id="nb-gsm" type="number" min="1" value={gsm} onChange={(e) => setGsm(e.target.value)} />
                </div>
                <div className="field">
                  <label className="req">Sheet size (cm)</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="number" min="1" step="0.1" value={length} placeholder="91.5" aria-label="Length in cm"
                           onChange={(e) => setLength(e.target.value)} />
                    <span className="dim">×</span>
                    <input type="number" min="1" step="0.1" value={width} placeholder="61" aria-label="Width in cm"
                           onChange={(e) => setWidth(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="hint" style={{ margin: '-6px 0 12px' }}>
                {existing ? `${existing.name} is already in your list — this goes into it.`
                  : newName ? `Adds ${newName} to your boards.`
                  : 'Size in either order — it is kept longer side first.'}
              </div>
            </div>
          )}

          {type === 'ADJUST' && (
            <div className="field">
              <label className="req">The count found</label>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
                  <input type="radio" name="adj-dir" aria-label="Fewer sheets than recorded"
                         checked={direction === 'OUT'} onChange={() => setDirection('OUT')} />
                  Fewer sheets than recorded
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
                  <input type="radio" name="adj-dir" aria-label="More sheets than recorded"
                         checked={direction === 'IN'} onChange={() => setDirection('IN')} />
                  More sheets than recorded
                </label>
              </div>
            </div>
          )}

          <div className="row2">
            <div className="field">
              <label htmlFor="mv-qty" className="req">
                {type === 'ADJUST' ? 'By how many sheets' : 'Sheets'}
              </label>
              <input id="mv-qty" type="number" min="1" step="1" value={qty}
                     autoFocus={!!fixedItemId} onChange={(e) => setQty(e.target.value)} />
              {type !== 'RECEIVE' && (
                <div className="hint">
                  {n > 0 && after !== null && after >= 0 ? `${fmt(after)} on hand after this` : ' '}
                </div>
              )}
            </div>
            <div className="field">
              <label htmlFor="mv-date">Date</label>
              <input id="mv-date" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>

          {type === 'RECEIVE' && (
            <>
              <div className="field">
                <label htmlFor="mv-sup">Supplier</label>
                <select id="mv-sup" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                  <option value="">—</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="row2">
                <div className="field">
                  <label htmlFor="mv-bill">Bill / DC no.</label>
                  <input id="mv-bill" type="text" value={billNo} onChange={(e) => setBillNo(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="mv-price">Price per kg (₹)</label>
                  <input id="mv-price" type="number" min="0" step="0.01" value={price}
                         onChange={(e) => setPrice(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {type !== 'ADJUST' && (
            <div className="field">
              <label htmlFor="mv-job">{type === 'RECEIVE' ? 'For job (optional)' : 'Job (optional)'}</label>
              <SearchSelect id="mv-job" value={jobId} onChange={setJobId}
                            placeholder="Type a job number, company or box"
                            emptyLabel={type === 'RECEIVE' ? 'None — general stock' : 'No job'}
                            options={jobs.map((j) => ({
                              value: String(j.job_id), label: `${j.job_number} · ${j.customer_name}`, hint: j.box_name,
                            }))} />
              {type === 'RECEIVE' && (
                <div className="hint">
                  {jobId
                    ? 'Pairs these sheets to the job: they stay with it, and no other job can take them.'
                    : 'Pick a job if this delivery was bought for one. Otherwise the first job to production takes it.'}
                </div>
              )}
            </div>
          )}

          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="mv-reason" className={type === 'ADJUST' ? 'req' : undefined}>
              {type === 'ADJUST' ? 'Reason' : 'Note'}
            </label>
            <input id="mv-reason" type="text" value={reason}
                   placeholder={type === 'ADJUST' ? 'e.g. Monthly count, damaged in storage'
                     : type === 'WASTE' ? 'e.g. Misprint, damaged' : ''}
                   onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <div className="modal-foot">
          {problem && <span className="hint" style={{ marginRight: 'auto' }}>{problem}</span>}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          {pairedWarning ? (
            <button className="btn primary" onClick={() => void submit(true)} disabled={busy || !!problem}>
              {busy ? 'Saving…' : 'Record anyway'}
            </button>
          ) : (
            <button className="btn primary" onClick={() => void submit()} disabled={busy || !!problem}>
              {busy ? 'Saving…' : ACTION[type].button}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- history

function HistoryTab() {
  const list = useList<Movement>('/api/stock/board-movements', 'movements',
                                 { sort: 'movement_date', dir: 'desc' });
  const columns: Column<Movement>[] = [
    { key: 'movement_date', label: 'Date', type: 'date',
      render: (m) => <span className="dim" style={{ whiteSpace: 'nowrap' }}>{fmtDate(m.movement_date)}</span> },
    { key: 'item_name', label: 'Board' },
    { key: 'movement_type', label: 'What', type: 'choice',
      choices: (Object.keys(MOVE_LABEL) as LedgerType[]).map((k) => ({ value: k, label: MOVE_LABEL[k] })),
      render: (m) => <MoveTag m={m} /> },
    { key: 'change', label: 'Sheets', type: 'number', align: 'right',
      render: (m) => <strong>{signed(m.change)}</strong> },
    { key: 'balance_after', label: 'Balance', type: 'number', align: 'right',
      render: (m) => <span className="dim">{fmt(m.balance_after)}</span> },
    { key: 'job_number', label: 'Job',
      render: (m) => (m.job_number ? <><span className="mono">{m.job_number}</span><MakeTag number={m.job_number} /></> : <span className="dim">—</span>) },
    { key: 'supplier_name', label: 'Supplier', secondary: true,
      render: (m) => <span className="dim">{m.supplier_name ?? '—'}</span> },
    { key: 'bill_no', label: 'Bill', secondary: true,
      render: (m) => <span className="dim">{m.bill_no ?? '—'}</span> },
    { key: 'reason', label: 'Reason / note', secondary: true,
      render: (m) => <span className="dim">{m.reason ?? '—'}</span> },
    { key: 'entered_by_name', label: 'By',
      render: (m) => <span className="dim" title={fmtTimestamp(m.created_at)}>{m.entered_by_name}</span> },
  ];
  return (
    <DataTable list={list} columns={columns} rowKey={(m) => m.movement_id}
               rowClass={(m) => (m.reversed_by_id ? 'struck' : '')}
               exportName="/api/stock/board-movements"
               empty="No board movements yet." />
  );
}
